import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiResponse } from "@/types/Api";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
// import PhoneInput from "react-phone-input-2";
import SignatureCanvas from "react-signature-canvas";
import { NewClient, SkipKYC } from "./NewClientSettings";
import GenericApi from "@/api/genericApi";
import { useNavigate } from "react-router-dom";
import { RoutesPathList } from "@/config/routes/Routes.tsx";
import { saveData } from "@/utils/localStorage.tsx";
import { clientPosKey } from "@/config/constants/StorageKeys.tsx";
import { NATIONALITIES } from "@/data/data.tsx";
import bgSNS from "../assets/SVG/bgSNS.svg";
import { isSNS } from "@/utils/utils";
import { useSnackBarAlert } from "@/hooks/useSnackbar.tsx";
import MuiPhoneNumber from "mui-phone-number";

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
}) => {
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter((opt) =>
    opt.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (selected: string) => {
    onChange(selected);
    setSearch("");
    setShowDropdown(false);
  };

  return (
    <div className="relative w-[23.125rem]" ref={dropdownRef}>
      <input
        type="text"
        // className="w-[21.125rem] h-[4.1875rem] rounded-[0.625rem] text-white border-[#363636] bg-[#272727] mt-[0.7rem] focus:ring-[#363636] focus:border-[#363636]"
        className={`rounded-[0.625rem]  mt-[0.7rem] ${
          isSNS()
            ? "w-[21.125rem] h-[4.1875rem] bg-[#272727] border-[#363636] text-white focus:ring-[#363636] focus:border-[#363636]"
            : "w-[20.8rem] h-[4.1875rem] bg-white text-black border-[#D9D9D9] focus:ring-[#202A43] focus:border-[#202A43]"
        }`}
        value={search || value}
        placeholder="Select nationality"
        onFocus={() => setShowDropdown(true)}
        onChange={(e) => {
          setSearch(e.target.value);
          setShowDropdown(true);
        }}
      />

      {showDropdown && (
        <ul
          className={`crypto-list2 absolute  ml-[1rem] z-10 w-[21.125rem]  border border-[#363636] rounded-md mt-1 max-h-[12rem] overflow-y-auto shadow-md ${
            isSNS()
              ? "text-white bg-[#272727]"
              : "text-black bg-white border-[#D9D9D9]"
          }`}
          style={
            {
              "--scrollbar-thumb-color": !isSNS() ? "#202A43" : "#8AFEFD",
              "--scrollbar-track-color": !isSNS() ? "#f7f4ed" : "#1C1C1C",
            } as React.CSSProperties
          }
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <li
                key={option}
                className={`px-4 text-start py-2 cursor-pointer ${
                  isSNS() ? "hover:bg-[#363636]" : "hover:bg-[#D9D9D9]"
                }`}
                onClick={() => handleSelect(option)}
              >
                {option}
              </li>
            ))
          ) : (
            <li className="px-4 py-2 text-gray-400">No results</li>
          )}
        </ul>
      )}
    </div>
  );
};

const FormPOS: React.FC = () => {
  const [toSign, setToSign] = useState<boolean>(false);

  const [hasSigned, setHasSigned] = useState<boolean>(false);

  const sigRef = useRef<SignatureCanvas>(null);

  const navigate = useNavigate();

  const [hasStepped, setHasStepped] = useState(false);

  // const { showSnackBar } = useSnackBarAlert()

  const clearSignature = () => {
    sigRef.current?.clear();
  };

  const saveSignature = () => {
    setHasSigned(true);
    const nextPath = isSNS() ? "/pos/kyc-sns" : "/pos/kyc";
    navigate(nextPath);
  };

  const [formPOS, setFormPOS] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    nationality: "",
    birth_date: "",
  });

  const { showSnackBar } = useSnackBarAlert();

  type SkipKYCclient = Omit<SkipKYC, "id">;
  const submitSkipKyc = useMutation<
    ApiResponse<NewClient>,
    Error,
    SkipKYCclient
  >({
    mutationFn: (_data) => {
      const newClientData: SkipKYCclient = {};
      return GenericApi.post(`${ApiRoutes.SKIPKYC}`, newClientData);
    },
    onSuccess: ({ data }) => {
      navigate(RoutesPathList.ExchangePhone, { replace: true });
      saveData(clientPosKey, { client_id: data?.id ?? null });
    },
    onError: (error) => {
      console.error("Eroare la trimiterea formularului:", error);
    },
  });

  const handleSkipKyc = (isKyc: boolean) => {
    if (isKyc) {
      submitSkipKyc.mutate(
        {},
        {
          onSuccess: () => {
            setToSign(true);
          },
        }
      );
    } else {
      navigate(
        isSNS() ? RoutesPathList.ExchangeSNS : RoutesPathList.ExchangePhone
      );
    }
  };

  const handleNextStep = (isKyc: boolean) => {
    if (hasSigned || isKyc) {
      setHasSigned(false);
      navigate(
        isSNS() ? RoutesPathList.ExchangeSNS : RoutesPathList.ExchangePhone
      );
      return;
    }

    const hasEmptyFields = Object.values(formPOS).some((value) => value === "");

    if (hasEmptyFields && !isKyc) {
      showSnackBar(
        "All fields are required",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
      return;
    }

    submitFormPOSMutation.mutate(formPOS, {
      onSuccess: () => {
        setToSign(true);
      },
    });
  };

  type NewClientPayload = Omit<NewClient, "id" | "owner_id" | "created_at">;

  useEffect(() => {
    document.body.className = isSNS() ? "theme-sns" : "";
  }, [isSNS()]);

  const submitFormPOSMutation = useMutation<
    ApiResponse<NewClient>,
    Error,
    NewClientPayload
  >({
    mutationFn: (data) => {
      const hasEmptyFields = Object.values(formPOS).some(
        (value) => value === ""
      );

      console.log("Form data:", data);

      const newClientData: NewClientPayload = {
        birth_date: new Date().toISOString().split("T")[0],
        first_name: "Occasional",
        last_name: "Trader",
        email: `test${Math.floor(Math.random() * 1000)}@example.com`,
        nationality:
          NATIONALITIES[Math.floor(Math.random() * NATIONALITIES.length)],
        phone: "+40712345678",
      };

      return GenericApi.post(
        `${ApiRoutes.USERCLIENTS}`,
        hasEmptyFields ? newClientData : data
      );
    },
    onSuccess: ({ data }) => {
      const hasEmptyFields = Object.values(formPOS).some(
        (value) => value === ""
      );

      if (hasEmptyFields) {
        navigate(RoutesPathList.ExchangePhone, { replace: true });
        return;
      }

      saveData(clientPosKey, {
        client_id: data?.id ?? null,
      });
      setFormPOS({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        nationality: "",
        birth_date: "",
      });
    },
    onError: (error) => {
      console.error("Eroare la trimiterea formularului:", error);
    },
  });

  return !isSNS() ? (
    <div className="overflow-hidden min-h-screen w-full bg-[#F3F3F3] pb-[2.6rem] flex flex-col justify-between items-center font-satoshi">
      <div className="flex felx-row gap-3 mt-[3rem]">
        <div className="border border-[#202A43] bg-[#202A43] rounded-[0.25rem] w-[6.3rem] h-[0.6875rem]"></div>
        <div className="border border-[#202A43] bg-[#202A43] rounded-[0.25rem] w-[6.3rem] h-[0.6875rem]"></div>
        <div
          className={`border ${
            toSign ? "bg-[#202A43]" : "bg-[#D9D9D9] border-[#D9D9D9]"
          }  border-[#202A43] rounded-[0.25rem] w-[6.3rem] h-[0.6875rem]`}
        ></div>
      </div>

      <button
        className="flex mt-[2.5rem] mr-[19rem]"
        onClick={() => navigate("/pos/new-client-pos")}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="26"
          viewBox="0 0 16 26"
          fill="none"
        >
          <path
            d="M13.5 2L3 14L13.5 24"
            stroke="#202A43"
            stroke-width="4"
            stroke-linecap="round"
          />
        </svg>
      </button>

      <form>
        <div className={`flex flex-col items-center ${toSign ? "hidden" : ""}`}>
          <div className="text-[1.1875rem] font-[500] text-[#05004E] text-center mt-[0.5rem] mb-[0.5rem]">
            Name
          </div>
          <div>
            <input
              type="text"
              className="w-[21.125rem] h-[4.1875rem] px-[1rem] rounded-[0.625rem] border-[#D9D9D9] mt-[0.3rem] focus:ring-[#202A43] focus:border-[#202A43]"
              placeholder="Family Name"
              value={formPOS.last_name}
              onChange={(e) =>
                setFormPOS({
                  ...formPOS,
                  last_name: e.target.value,
                })
              }
            />
          </div>
          <div>
            <input
              type="text"
              className="w-[21.125rem] h-[4.1875rem] px-[1rem] rounded-[0.625rem] border-[#D9D9D9] mt-[0.7rem] focus:ring-[#202A43] focus:border-[#202A43]"
              placeholder="Given Name"
              value={formPOS.first_name}
              onChange={(e) =>
                setFormPOS({
                  ...formPOS,
                  first_name: e.target.value,
                })
              }
            />
          </div>

          <div className="w-[20.8rem] text-center mt-[2rem] mb-[0.5rem]">
            <label className="text-[1.1875rem] font-[500] text-[#05004E] ">
              Date of birth
            </label>
            <input
              type="date"
              name="birth_date"
              value={formPOS.birth_date}
              onChange={(e) =>
                setFormPOS({
                  ...formPOS,
                  birth_date: e.target.value,
                })
              }
              max={
                new Date(new Date().setFullYear(new Date().getFullYear() - 18))
                  .toISOString()
                  .split("T")[0]
              }
              defaultValue={new Date().toISOString().split("T")[0]}
              className="mt-1 block w-full border-[#D9D9D9] rounded-[0.625rem] focus:ring-[#202A43] focus:border-[#202A43] sm:text-sm"
            />
          </div>

          <div className="w-[22.9rem] text-center mt-[2rem] mb-[0.5rem]">
            <label className="text-[1.1875rem] font-[500] text-[#05004E]">
              Nationality
            </label>
            <SearchableSelect
              value={formPOS.nationality}
              onChange={(val) => setFormPOS({ ...formPOS, nationality: val })}
              options={NATIONALITIES}
            />
          </div>

          <div className="w-[20.8rem] flex flex-col justify-between">
            <label className="text-[1.1875rem] font-[500] text-[#05004E] text-center mt-[2rem] mb-[0.5rem]">
              Phone number
            </label>
            <MuiPhoneNumber
              defaultCountry="gi"
              value={formPOS.phone}
              onChange={(value) =>
                setFormPOS({
                  ...formPOS,
                  phone: typeof value === "string" ? value : "",
                })
              }
              inputClass="input_design Input w-full"
              sx={{
                "& .MuiInputBase-input": {
                  width: "15rem",
                  fontSize: "0.875rem !important",
                  color: "#000 !important",
                  ":focus": {
                    border: "none !important",
                    boxShadow: "none !important",
                    outline: "none !important",
                    color: "#000 !important",
                  },
                  ":hover": {
                    color: "#000 !important",
                  },
                },
                "& .MuiInputBase-input, & .MuiInputBase-input:focus, & .MuiInputBase-input:hover, & .MuiInputBase-input:active":
                  {
                    color: "#000 !important",
                  },
                ".MuiInputBase-root": {
                  width: "100%",
                  paddingY: "0.31rem",
                  color: "#000 !important",
                  "& input": {
                    color: "#000 !important",
                  },
                  "::before": {
                    borderBottom: "none !important",
                    display: "none !important",
                  },
                  "::after": {
                    borderBottom: "none !important",
                    display: "none !important",
                  },
                  ":hover::before": {
                    borderBottom: "none !important",
                  },
                  ":focus-within::after": {
                    borderBottom: "none !important",
                  },
                  ":hover:not(.Mui-disabled):before": {
                    borderBottom: "none !important",
                  },
                },
                "& .MuiInput-root::before": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                "& .MuiInput-root::after": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                "& .MuiInput-root:hover::before": {
                  borderBottom: "none !important",
                },
                "& .MuiInput-root.Mui-focused::after": {
                  borderBottom: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root::before": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before":
                  {
                    borderBottom: "none !important",
                  },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root.Mui-focused::after":
                  {
                    borderBottom: "none !important",
                  },
                "& .MuiInputAdornment-root": {
                  marginLeft: "0.6rem !important",
                  paddingLeft: "0.4rem !important",
                  width: "2rem !important",
                },
                ".css-1yrc8ca-MuiInputBase-input-MuiInput-input, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:focus, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:hover":
                  {
                    fontSize: "0.875rem !important",
                    color: "#000 !important",
                  },
              }}
            />
          </div>

          <div className="text-center mt-[2rem] mb-[3rem] flex flex-col">
            <label className="text-[1.1875rem] font-[500] text-[#05004E]">
              Email
            </label>
            <input
              type="email"
              className="w-[21.125rem] h-[4.1875rem] rounded-[0.625rem] border-[#D9D9D9] mt-[0.7rem] focus:ring-[#202A43] focus:border-[#202A43]"
              placeholder="Email address"
              value={formPOS.email}
              onChange={(e) =>
                setFormPOS({
                  ...formPOS,
                  email: e.target.value,
                })
              }
            />
          </div>
        </div>
        {/* <div
          className={`flex flex-col items-start px-[3rem] ${
            toSign ? "" : "hidden"
          }`}
        >
          <div className="font-satoshi text-[#737791] text-[1.1875rem] font-[500] text-start mt-[2rem] mx-[2rem] mb-[1rem]">
            By signing this document, I confirm that the information provided is
            accurate and complete. I also agree to the application's terms and
            conditions, including the KYC and AML policy
          </div>
          <SignatureCanvas
            ref={sigRef}
            penColor="black"
            canvasProps={{
              width: 370,
              height: 170,
              className: "border border-[#202A43] rounded-[0.6875rem] bg-white !w-[22.1875rem]",
            }}
          />
          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={clearSignature}
              className="font-satoshi text-[1.1875rem] font-[500] text-red-500 px-2 py-1 rounded"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={saveSignature}
              className="bg-[#222b45] rounded-[16px] px-[2rem] py-[0.5rem] text-[white] font-satoshi text-[1.1875rem] font-[500] border-none"
            >
              Save
            </button>
          </div>
        </div> */}
        <div
          className={`flex flex-col items-start px-[3rem] ${
            toSign ? "" : "hidden"
          }`}
        >
          <div className="text-[#737791] text-[1.1875rem] font-[400] text-start mb-[1rem] mt-[2rem]">
            By signing this document, I confirm that the information provided is
            accurate and complete. I also agree to the application's terms and
            conditions, including the KYC and AML policy
          </div>
          <SignatureCanvas
            ref={sigRef}
            penColor="black"
            canvasProps={{
              width: 320,
              height: 170,
              className:
                "border border-[##202A43] rounded-[0.6875rem] bg-[#fff] mt-[1rem]",
            }}
          />
          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={clearSignature}
              className="font-[helvetica] text-[1.1875rem] font-[400] text-[#FF3838] px-2 py-1 rounded"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={saveSignature}
              className="bg-[#222b45] rounded-[16px] px-[2rem] py-[0.5rem] text-[#fff] font-satoshi text-[1.1875rem] font-[500] border-none"
            >
              Confirm
            </button>
          </div>
        </div>
      </form>

      {/* <button
        type="button"
        className={`button--blue w-[19.4375rem] !h-[3.5625rem] !mt-[4rem] font-[500]`}
        onClick={() => {
          if (toSign) {
            saveSignature();
          }
          handleNextStep(false);
        }}
      >
        {toSign ? "Confirm" : "Next Step"}
      </button> */}
      {!hasStepped && (
        <button
          type="button"
          className={`button--blue w-[19.4375rem] !h-[3.5625rem] !mt-[4rem] font-[500]`}
          onClick={() => {
            setHasStepped(true);
            handleNextStep(false);
          }}
        >
          Next Step
        </button>
      )}
      {/* <button
        type="submit"
        className="button--blue !w-[10.3125rem] !h-[3.3125rem] !mt-[1rem]"
        onClick={handleNextStep}
      >
        Next Step
      </button> */}
      <button
        type="submit"
        className="w-full !h-[3.3125rem] !mt-[1rem]  !text-[#202A43] font-[600] text-[1rem]"
        onClick={() => handleSkipKyc(true)}
      >
        Skip KYC
      </button>
    </div>
  ) : (
    <div
      className="overflow-hidden min-h-screen w-full bg-[#1C1C1C] pb-[2.6rem] flex flex-col justify-between items-center"
      style={{
        backgroundImage: `url(${bgSNS})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="flex flex-row gap-3 mt-[3rem]">
        <div className="border border-[#8AFEFD] bg-[#8AFEFD] rounded-[0.25rem] w-[6.3rem] h-[0.6875rem]"></div>
        <div className="border border-[#8AFEFD] bg-[#8AFEFD] rounded-[0.25rem] w-[6.3rem] h-[0.6875rem]"></div>
        <div
          className={`border ${
            toSign ? "bg-[#8AFEFD]" : "bg-[#303030] border-[#303030]"
          }  border-[#8AFEFD] rounded-[0.25rem] w-[6.3rem] h-[0.6875rem]`}
        ></div>
      </div>
      <button
        className="flex mt-[2.5rem] mr-[19rem]"
        onClick={() => navigate("/pos/new-client-pos-sns")}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="26"
          viewBox="0 0 16 26"
          fill="none"
        >
          <path
            d="M13.5 2L3 14L13.5 24"
            stroke="#8AFEFD"
            stroke-width="4"
            stroke-linecap="round"
          />
        </svg>
      </button>
      <form>
        <div className={`flex flex-col items-center ${toSign ? "hidden" : ""}`}>
          <div className="text-[1.1875rem] font-[400] text-[#fff] text-center mt-[0.5rem] mb-[0.5rem] font-[helvetica]">
            Name
          </div>
          <div>
            <input
              type="text"
              className="w-[21.125rem] h-[4.1875rem] px-[1rem] rounded-[0.625rem] text-white border-[#363636] bg-[#272727] mt-[0.3rem] focus:ring-[#363636] focus:border-[#363636]"
              placeholder="Family Name"
              value={formPOS.last_name}
              onChange={(e) =>
                setFormPOS({
                  ...formPOS,
                  last_name: e.target.value,
                })
              }
            />
          </div>
          <div>
            <input
              type="text"
              className="w-[21.125rem] h-[4.1875rem] rounded-[0.625rem] text-white border-[#363636] bg-[#272727] mt-[0.7rem] focus:ring-[#363636] focus:border-[#363636]"
              placeholder="Given Name"
              value={formPOS.first_name}
              onChange={(e) =>
                setFormPOS({
                  ...formPOS,
                  first_name: e.target.value,
                })
              }
            />
          </div>

          <div className="w-[20.8rem] text-center mt-[2rem] mb-[0.5rem]">
            <label className="text-[1.1875rem] font-[400] text-[#fff] font-[helvetica]">
              Date of birth
            </label>
            <input
              type="date"
              name="birth_date"
              value={formPOS.birth_date}
              onChange={(e) =>
                setFormPOS({
                  ...formPOS,
                  birth_date: e.target.value,
                })
              }
              max={
                new Date(new Date().setFullYear(new Date().getFullYear() - 18))
                  .toISOString()
                  .split("T")[0]
              }
              defaultValue={new Date().toISOString().split("T")[0]}
              className="mt-1 text-[#fff] block w-full border-[#363636] bg-[#272727] rounded-[0.625rem] focus:ring-[#363636] focus:border-[#363636] sm:text-sm"
            />

            <style>
              {`
                input[type="date"]::-webkit-calendar-picker-indicator {
                filter: invert(1);
                  }
              `}
            </style>
          </div>

          <div className="w-[22.8rem] text-center mt-[2rem] mb-[0.5rem]">
            <label className="text-[1.1875rem] font-[400] text-[#fff] font-[helvetica]">
              Nationality
            </label>
            <SearchableSelect
              value={formPOS.nationality}
              onChange={(val) => setFormPOS({ ...formPOS, nationality: val })}
              options={NATIONALITIES}
            />
          </div>

          <div className="w-[20.8rem] flex flex-col justify-between font-[helvetica]">
            <label className="text-[1.1875rem] font-[500] text-[#fff] text-center mt-[2rem] mb-[0.5rem]">
              Phone number
            </label>
            {/* <PhoneInput
              country={"ro"}
              value={formPOS.phone}
              onChange={(phone) => setFormPOS({ ...formPOS, phone })}
              inputClass="!block !w-full !border-[#363636] !bg-[#272727] !rounded-md text-white focus:ring-[#363636] focus:border-[#363636] !sm:text-sm"
              enableSearch
              countryCodeEditable={false}
            /> */}
            <MuiPhoneNumber
              defaultCountry="gi"
              value={formPOS.phone}
              onChange={(value) =>
                setFormPOS({
                  ...formPOS,
                  phone: typeof value === "string" ? value : "",
                })
              }
              inputClass="!block !w-full !border-[#363636] !bg-[#272727] !rounded-md text-white !focus:bg-red-600 !focus:ring-[#363636] !focus:border-[#363636] !sm:text-sm"
              sx={{
                "& .MuiInputBase-input": {
                  width: "15rem",
                  fontSize: "0.875rem !important",
                  color: "#fff !important",
                  ":focus": {
                    border: "none !important",
                    boxShadow: "none !important",
                    outline: "none !important",
                    color: "#fff !important",
                  },
                  ":hover": {
                    color: "#fff !important",
                  },
                },
                "& .MuiInputBase-input, & .MuiInputBase-input:focus, & .MuiInputBase-input:hover, & .MuiInputBase-input:active":
                  {
                    color: "#fff !important",
                  },
                ".MuiInputBase-root": {
                  width: "100%",
                  paddingY: "0.31rem",
                  color: "#fff !important",
                  "& input": {
                    color: "#fff !important",
                  },
                  "::before": {
                    borderBottom: "none !important",
                    display: "none !important",
                  },
                  "::after": {
                    borderBottom: "none !important",
                    display: "none !important",
                  },
                  ":hover::before": {
                    borderBottom: "none !important",
                  },
                  ":focus-within::after": {
                    borderBottom: "none !important",
                  },
                  ":hover:not(.Mui-disabled):before": {
                    borderBottom: "none !important",
                  },
                },
                "& .MuiInput-root::before": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                "& .MuiInput-root::after": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                "& .MuiInput-root:hover::before": {
                  borderBottom: "none !important",
                },
                "& .MuiInput-root.Mui-focused::after": {
                  borderBottom: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root::before": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before":
                  {
                    borderBottom: "none !important",
                  },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root.Mui-focused::after":
                  {
                    borderBottom: "none !important",
                  },
                "& .MuiInputAdornment-root": {
                  marginLeft: "0.6rem !important",
                  paddingLeft: "0.4rem !important",
                  width: "2rem !important",
                },
                ".css-1yrc8ca-MuiInputBase-input-MuiInput-input, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:focus, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:hover":
                  {
                    fontSize: "0.875rem !important",
                    color: isSNS() ? "#fff !important" : "#000 !important",
                  },
                ".MuiPaper-root": {
                  backgroundColor: "#2B2B2B !important",
                  color: "#fff !important",
                },
                ".MuiMenuItem-root": {
                  color: "#fff !important",
                  "&:hover": {
                    backgroundColor: "#1a1a1a !important",
                  },
                },
                ".Mui-selected": {
                  backgroundColor: "#202a43 !important",
                },
              }}
            />
          </div>

          <div className="text-center mt-[2rem] mb-[3rem] flex flex-col font-[helvetica]">
            <label className="text-[1.1875rem] font-[500] text-[#fff]">
              Email
            </label>
            <input
              type="email"
              className="w-[21.125rem] h-[4.1875rem] rounded-[0.625rem] border-[#363636] bg-[#272727] text-white mt-[0.7rem] focus:ring-[#363636] focus:border-[#363636]"
              placeholder="Email address"
              value={formPOS.email}
              onChange={(e) =>
                setFormPOS({
                  ...formPOS,
                  email: e.target.value,
                })
              }
            />
          </div>
        </div>
        <div
          className={`flex flex-col items-start px-[3rem] ${
            toSign ? "" : "hidden"
          }`}
        >
          <div className="font-[helvetica] text-[#6B6B6B] text-[1.1875rem] font-[400] text-start mb-[1rem] mt-[2rem]">
            By signing this document, I confirm that the information provided is
            accurate and complete. I also agree to the application's terms and
            conditions, including the KYC and AML policy
          </div>
          <SignatureCanvas
            ref={sigRef}
            penColor="black"
            canvasProps={{
              width: 320,
              height: 170,
              className:
                "border border-[#4B4B4B] rounded-[0.6875rem] bg-[#313131] mt-[1rem]",
            }}
          />
          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={clearSignature}
              className="font-[helvetica] text-[1.1875rem] font-[400] text-[#FF3838] px-2 py-1 rounded"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={saveSignature}
              className="bg-[#8AFEFD] rounded-[16px] px-[2rem] py-[0.5rem] text-[#1C1C1C1] font-satoshi text-[1.1875rem] font-[500] border-none"
            >
              Confirm
            </button>
          </div>
        </div>
      </form>
      {/* <button
        type="button"
        className={`button__green w-[19.4375rem] !h-[3.5625rem] !mt-[4rem] !bg-[#8AFEFD] !text-[black] font-[helvetica] font-[600]`}
        onClick={() => {
          // if (toSign) {
          //   saveSignature();
          // }
          handleNextStep(false);
        }}
      >
        Next Step
      </button> */}

      {!hasStepped && (
        <button
          type="button"
          className={`button__green w-[19.4375rem] !h-[3.5625rem] !mt-[4rem] !bg-[#8AFEFD] !text-[black] font-[helvetica] font-[600]`}
          onClick={() => {
            setHasStepped(true);
            handleNextStep(false);
          }}
        >
          Next Step
        </button>
      )}
      {/* <button
        type="submit"
        className="button--blue !w-[10.3125rem] !h-[3.3125rem] !mt-[1rem]"
        onClick={handleNextStep}
      >
        Next Step
      </button> */}
      <button
        type="submit"
        className="w-full !h-[3.3125rem] !mt-[1rem]  !text-[#8AFEFD] font-[helvetica] font-[600] text-[1rem]"
        onClick={() => handleSkipKyc(true)}
      >
        Skip KYC
      </button>
    </div>
  );
};

export default FormPOS;
